import React, { useCallback, useEffect, useState } from 'react'
import { httpClient } from 'services/api/dataProvider/common'
import { I18nFile } from 'services/geo-management'
import I18nFormHandler from './I18nFormHandler'

const API_URL = process.env.REACT_APP_API_BASE_URL

type PlatformCode = 'be' | 'fe'
type Props = {
  platformCode: PlatformCode
  requiredFiles: I18nFile[]
}

export type I18nJson = Record<string, string>
export type I18nMap = Record<string, I18nJson>
export type UpdateFileFunction = (
  bundle: I18nMap
) => (e: unknown) => Promise<void>

type I18nDelete = {
  path: string
  op: 'remove'
}
type I18nUpsert = {
  path: string
  op: 'add'
  value: string
}

type I18nUpdateRequest = Array<I18nDelete | I18nUpsert>

export const getI18nBundleOperations = (
  old: I18nJson,
  current: I18nJson
): I18nUpdateRequest | undefined => {
  // get deleted items
  const deletes = Object.keys(old)
    .filter((key) => current[key] === undefined)
    .map((value: string): I18nDelete => {
      return {
        path: value,
        op: 'remove',
      }
    })

  const upserts = Object.keys(current)
    .filter((key) => old[key] !== current[key])
    .map((item): I18nUpsert => {
      return { path: item, value: current[item], op: 'add' }
    })

  return deletes.length || upserts.length ? [...deletes, ...upserts] : undefined
}

const I18nFileProvider: React.FC<Props> = ({ platformCode, requiredFiles }) => {
  const [apiI18nBundle, setApiI18nBundle] = useState<I18nMap | undefined>()
  const [isSaving, setIsSaving] = useState(false)

  const fetchFile = useCallback(async () => {
    try {
      const responses = await Promise.all(
        requiredFiles.map((f) =>
          httpClient(`${API_URL}/i18n/${platformCode}-${f}`, { method: 'GET' })
        )
      )
      setApiI18nBundle(
        requiredFiles.reduce(
          (acc, code, idx) => ({ ...acc, [code]: responses[idx].json }),
          {}
        )
      )
    } catch (error) {
      console.error(`ERROR fetching i18n files for ${platformCode}: ${error}`)
    }
  }, [platformCode, requiredFiles])

  const updateFile: UpdateFileFunction = useCallback(
    (bundle) => async (_e) => {
      const batchI18nOps = (code: string, ops: I18nUpdateRequest) => {
        return httpClient(`${API_URL}/i18n/${platformCode}-${code}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json-patch+json',
          },
          body: JSON.stringify(ops),
        })
      }
      try {
        setIsSaving(true)
        await Promise.all(
          Object.keys(bundle).map<Promise<any>>((code) => {
            const ops = getI18nBundleOperations(
              apiI18nBundle![code],
              bundle[code]
            )
            if (!!ops) {
              return batchI18nOps(code, ops)
            } else {
              return Promise.resolve()
            }
          })
        )
        setApiI18nBundle({ ...bundle })
      } catch (error) {
        console.error(`ERROR updating i18n files for ${platformCode}: ${error}`)
      } finally {
        setIsSaving(false)
      }
    },
    [apiI18nBundle, platformCode]
  )

  useEffect(() => {
    fetchFile()
  }, [fetchFile])

  return !!apiI18nBundle ? (
    <I18nFormHandler
      apiI18nBundle={apiI18nBundle}
      saveFunction={updateFile}
      isSaving={isSaving}
      requiredFiles={requiredFiles}
    />
  ) : null
}

export default I18nFileProvider
